import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import type { AppThunkDispatch } from '@src/providers/AppStateProvider/types';
import type { DealBookingType } from '@src/types/OTC';
import { useCallback, useEffect, useRef } from 'react';
import { openManualDealTradeFormThunk } from '../../OMS/ManualDealTradeView/thunks/openManualDealTradeFormThunk';
import { waitForNewSymbolThunk } from '../../OMS/ManualDealTradeView/thunks/waitForNewSymbolThunk';
import { useDealProductBuilder } from '../DealProductBuilderProvider/useDealProductBuilder';
import { clearForm } from '../dealProductBuilderSlice';

interface UseSymbolPrimingResult {
  primeSymbol: (symbol: string) => Promise<void>;
}

// Return type of dispatching waitForNewSymbolThunk - used to track and cancel thunks
type WaitForNewSymbolResult = ReturnType<ReturnType<typeof waitForNewSymbolThunk>>;

/**
 * Hook to prime a symbol and navigate to the manual deal trade form when it becomes available.
 * Automatically cancels previous calls and cleans up on unmount.
 */
export const useSymbolPriming = (bookingType: DealBookingType): UseSymbolPrimingResult => {
  const appDispatch = useAppStateDispatch();
  const { dispatch: dealProductBuilderDispatch } = useDealProductBuilder();
  const currentThunkRef = useRef<WaitForNewSymbolResult | null>(null);

  const primeSymbol = useCallback(
    async (symbol: string): Promise<void> => {
      if (currentThunkRef.current) {
        currentThunkRef.current.abort();
        currentThunkRef.current = null;
      }

      const onSymbolAvailable = (dispatch: AppThunkDispatch) => {
        dispatch(
          openManualDealTradeFormThunk({
            launchData: {
              bookingType,
              templateSymbol: symbol,
            },
          })
        );
        dealProductBuilderDispatch(clearForm(bookingType));
      };

      const thunkPromise = appDispatch(
        waitForNewSymbolThunk({
          symbol,
          onSymbolAvailable,
        })
      );

      currentThunkRef.current = thunkPromise;

      // Clear ref when thunk completes (success or failure)
      thunkPromise.finally(() => {
        if (currentThunkRef.current === thunkPromise) {
          currentThunkRef.current = null;
        }
      });

      // unwrap() ensures we wait for onSymbolAvailable to complete
      await thunkPromise.unwrap();
    },
    [bookingType, appDispatch, dealProductBuilderDispatch]
  );

  // Cancel any pending priming on unmount
  useEffect(() => {
    return () => {
      if (currentThunkRef.current) {
        currentThunkRef.current.abort();
        currentThunkRef.current = null;
      }
    };
  }, []);

  return {
    primeSymbol,
  };
};
